/**
 * Stock Price Formatter Utility
 *
 * Handles currency-aware price formatting, including special handling for
 * UK stocks which report prices in pence (GBX currency code).
 */

// Currencies that represent pence (need /100 conversion to pounds for display)
const PENCE_CURRENCIES = ['GBX', 'GBp'];

// Exchange to currency mappings
const EXCHANGE_CURRENCIES: Record<string, string> = {
  // US Exchanges
  NASDAQ: 'USD',
  NYSE: 'USD',
  AMEX: 'USD',
  CBOE: 'USD', // Chicago Board Options Exchange
  OTC: 'USD', // Other OTC
  PNK: 'USD', // OTC Pink Slips
  // UK
  LSE: 'GBX', // London Stock Exchange
  AQS: 'GBX', // Aquis Stock Exchange
  IOB: 'GBX', // International Order Book
  // Euronext
  AMS: 'EUR', // Amsterdam
  PAR: 'EUR', // Paris
  MIL: 'EUR', // Milan/Italy
  BRU: 'EUR', // Brussels
  LIS: 'EUR', // Lisbon
  DUB: 'EUR', // Dublin
  // Germany
  XETRA: 'EUR', // Deutsche Börse
  FSX: 'EUR', // Frankfurt
  BER: 'EUR', // Berlin
  DUS: 'EUR', // Dusseldorf
  HAM: 'EUR', // Hamburg
  MUN: 'EUR', // Munich
  STU: 'EUR', // Stuttgart
  // Switzerland
  SIX: 'CHF', // Swiss Exchange
  // Nordic
  CPH: 'DKK', // Copenhagen (Denmark)
  STO: 'SEK', // Stockholm (Sweden)
  HEL: 'EUR', // Helsinki (Finland - uses EUR)
  OSL: 'NOK', // Oslo (Norway)
  ICE: 'ISK', // Iceland
  // Other European
  BME: 'EUR', // Madrid (Spain)
  VIE: 'EUR', // Vienna (Austria)
  WSE: 'PLN', // Warsaw (Poland)
  ATH: 'EUR', // Athens (Greece)
  PRA: 'CZK', // Prague (Czech Republic)
  BUD: 'HUF', // Budapest (Hungary)
  // Canada
  TSX: 'CAD', // Toronto
  TSXV: 'CAD', // Toronto Ventures
  CNQ: 'CAD', // Canadian Securities Exchange
  NEO: 'CAD', // CBOE CA
  // Asia Pacific
  JPX: 'JPY', // Tokyo (Japan)
  HKSE: 'HKD', // Hong Kong
  SHH: 'CNY', // Shanghai
  SHZ: 'CNY', // Shenzhen
  ASX: 'AUD', // Australia
  SES: 'SGD', // Singapore
  KSC: 'KRW', // Korea Exchange
  KOE: 'KRW', // KOSDAQ
  TAI: 'TWD', // Taiwan
  TWO: 'TWD', // Taipei Exchange
  KLS: 'MYR', // Malaysia
  SET: 'THB', // Thailand
  JKT: 'IDR', // Indonesia
  // Other
  JSE: 'ZAR', // Johannesburg (South Africa)
  JNB: 'ZAR', // Johannesburg alternate code
  BSE: 'INR', // Bombay
  NSE: 'INR', // National Stock Exchange India
  SAO: 'BRL', // Brazil
  MEX: 'MXN', // Mexico
  SAU: 'SAR', // Saudi Arabia
  TLV: 'ILS', // Tel Aviv (Israel)
  NZE: 'NZD', // New Zealand
};

// Currency symbol mappings
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  GBP: '£',
  GBX: '', // Handled specially (pence/pounds)
  EUR: '€',
  CAD: 'CA$',
  AUD: 'A$',
  JPY: '¥',
  CHF: 'CHF ',
  HKD: 'HK$',
  SGD: 'S$',
  INR: '₹',
  CNY: '¥',
  KRW: '₩',
  SEK: 'kr ',
  NOK: 'kr ',
  DKK: 'kr ',
  ZAR: 'R',
  BRL: 'R$',
  MXN: 'MX$',
  // Additional currencies
  PLN: 'zł', // Polish złoty
  CZK: 'Kč ', // Czech koruna
  HUF: 'Ft ', // Hungarian forint
  ISK: 'kr ', // Icelandic króna
  TWD: 'NT$', // New Taiwan dollar
  MYR: 'RM ', // Malaysian ringgit
  THB: '฿', // Thai baht
  IDR: 'Rp ', // Indonesian rupiah
  SAR: 'SAR ', // Saudi riyal
  ILS: '₪', // Israeli shekel
  NZD: 'NZ$', // New Zealand dollar
};

export interface FormatStockPriceOptions {
  /** The price value to format */
  price: number | null | undefined;
  /** The currency code (e.g., 'USD', 'GBX', 'EUR') */
  currency?: string;
  /** Number of decimal places (default: 2) */
  decimals?: number;
  /** Whether to include the currency symbol (default: true) */
  includeSymbol?: boolean;
}

/**
 * Get the currency code for a given stock exchange.
 * Falls back to USD if exchange is not recognized.
 *
 * @example
 * getCurrencyFromExchange('NYSE')  // 'USD'
 * getCurrencyFromExchange('LSE')   // 'GBX'
 * getCurrencyFromExchange('XETRA') // 'EUR'
 */
export const getCurrencyFromExchange = (exchange?: string): string => {
  if (!exchange) return 'USD';
  const upperExchange = exchange.toUpperCase();
  return EXCHANGE_CURRENCIES[upperExchange] ?? 'USD';
};

/**
 * Check if a currency code represents pence (needs conversion to pounds)
 */
export const isPenceCurrency = (currency?: string): boolean => {
  if (!currency) return false;
  return PENCE_CURRENCIES.includes(currency.toUpperCase()) ||
         PENCE_CURRENCIES.includes(currency);
};

/**
 * Format a UK price value that's in pence.
 *
 * Rules:
 * - < 100p: Show as pence (e.g., "45.50p")
 * - >= 100p: Convert to pounds (e.g., "£1.06")
 * - Large values: Include commas (e.g., "£1,500.00")
 */
export const formatUKPrice = (
  penceValue: number,
  decimals: number = 2,
  includeSymbol: boolean = true
): string => {
  // Prices under 100p: show as pence
  if (penceValue < 100) {
    const formatted = penceValue.toFixed(decimals);
    return includeSymbol ? `${formatted}p` : formatted;
  }

  // Prices >= 100p: convert to pounds
  const pounds = penceValue / 100;

  if (!includeSymbol) {
    return pounds >= 1000
      ? pounds.toLocaleString('en-GB', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
      : pounds.toFixed(decimals);
  }

  // Include £ symbol
  if (pounds >= 1000) {
    return `£${pounds.toLocaleString('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }

  return `£${pounds.toFixed(decimals)}`;
};

/**
 * Get the currency symbol for a given currency code
 */
export const getCurrencySymbol = (currency?: string): string => {
  if (!currency) return '$';
  const upperCurrency = currency.toUpperCase();

  // GBX is handled specially - return empty string as it uses formatUKPrice
  if (isPenceCurrency(currency)) return '';

  return CURRENCY_SYMBOLS[upperCurrency] ?? currency + ' ';
};

/**
 * Format a stock price with the appropriate currency symbol and formatting.
 *
 * Handles:
 * - UK stocks (GBX): Converts pence to pounds, uses £ or p symbol
 * - US stocks (USD): Uses $ symbol
 * - EU stocks (EUR): Uses € symbol
 * - Other currencies: Uses appropriate symbol or currency code
 *
 * @example
 * // US stock
 * formatStockPrice({ price: 150.50, currency: 'USD' }) // "$150.50"
 *
 * // UK stock under £1
 * formatStockPrice({ price: 99, currency: 'GBX' }) // "99.00p"
 *
 * // UK stock over £1
 * formatStockPrice({ price: 106, currency: 'GBX' }) // "£1.06"
 *
 * // UK stock large value
 * formatStockPrice({ price: 150000, currency: 'GBX' }) // "£1,500.00"
 */
export const formatStockPrice = (options: FormatStockPriceOptions): string => {
  const { price, currency, decimals = 2, includeSymbol = true } = options;

  // Handle null/undefined prices
  if (price == null || isNaN(price)) {
    // Return a default based on currency
    if (isPenceCurrency(currency)) {
      return includeSymbol ? '0.00p' : '0.00';
    }
    const symbol = includeSymbol ? getCurrencySymbol(currency) : '';
    return `${symbol}0.00`;
  }

  // Handle UK stocks (prices in pence)
  if (isPenceCurrency(currency)) {
    return formatUKPrice(price, decimals, includeSymbol);
  }

  // Handle all other currencies
  const symbol = includeSymbol ? getCurrencySymbol(currency) : '';

  // Format with locale for large numbers
  if (Math.abs(price) >= 1000) {
    const locale = currency === 'EUR' ? 'de-DE' : 'en-US';
    return `${symbol}${price.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }

  return `${symbol}${price.toFixed(decimals)}`;
};

/**
 * Format a price change value (e.g., +$1.50 or -£0.05)
 */
export const formatPriceChange = (options: FormatStockPriceOptions): string => {
  const { price, currency, decimals = 2, includeSymbol = true } = options;

  if (price == null || isNaN(price)) {
    return includeSymbol ? `${getCurrencySymbol(currency)}0.00` : '0.00';
  }

  // Sign goes in FRONT of the currency symbol (e.g. -$1,234.56, not $-1,234.56),
  // and the magnitude is comma-formatted for large changes — same as
  // formatStockPrice — so the number stays readable.
  const sign = price < 0 ? '-' : '+';
  const abs = Math.abs(price);
  const formatMagnitude = (value: number): string => {
    if (value >= 1000) {
      const locale = currency === 'EUR' ? 'de-DE' : 'en-US';
      return value.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }
    return value.toFixed(decimals);
  };

  // For GBX, convert the change to pounds if >= £1 change
  if (isPenceCurrency(currency)) {
    if (abs >= 100) {
      const symbol = includeSymbol ? '£' : '';
      return `${sign}${symbol}${formatMagnitude(abs / 100)}`;
    }
    // Small pence changes
    const formatted = formatMagnitude(abs);
    return includeSymbol ? `${sign}${formatted}p` : `${sign}${formatted}`;
  }

  const symbol = includeSymbol ? getCurrencySymbol(currency) : '';
  return `${sign}${symbol}${formatMagnitude(abs)}`;
};

/**
 * Format a value that's already in the display currency (not raw API pence).
 *
 * Use this for:
 * - Calculated projection values (EPS, share prices from financial statements)
 * - Financial statement data (revenue, net income) which FMP returns in GBP not GBX
 * - Any value that's already been converted or doesn't need pence→pounds conversion
 *
 * @example
 * // UK stock - value already in pounds
 * formatDisplayCurrencyValue({ price: 1.47, currency: 'GBX' }) // "£1.47"
 *
 * // US stock - same as formatStockPrice
 * formatDisplayCurrencyValue({ price: 150.50, currency: 'USD' }) // "$150.50"
 */
export const formatDisplayCurrencyValue = (options: FormatStockPriceOptions): string => {
  const { price, currency, decimals = 2, includeSymbol = true } = options;

  // Handle null/undefined prices
  if (price == null || isNaN(price)) {
    const symbol = includeSymbol ? getDisplayCurrencySymbol(currency) : '';
    return `${symbol}0.00`;
  }

  // Get the display currency symbol (£ for GBX, $ for USD, etc.)
  const symbol = includeSymbol ? getDisplayCurrencySymbol(currency) : '';

  // Format with locale for large numbers
  if (Math.abs(price) >= 1000) {
    const locale = currency === 'EUR' ? 'de-DE' : 'en-GB';
    return `${symbol}${price.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }

  return `${symbol}${price.toFixed(decimals)}`;
};

/**
 * Get the display currency symbol (converts GBX → £)
 */
export const getDisplayCurrencySymbol = (currency?: string): string => {
  if (!currency) return '$';
  const upperCurrency = currency.toUpperCase();

  // GBX (pence) displays as £
  if (isPenceCurrency(currency)) return '£';

  return CURRENCY_SYMBOLS[upperCurrency] ?? currency + ' ';
};

/**
 * Compact format for values that are already in their display (base) currency
 * — i.e. someone has already converted GBp → GBP for you. Use this for chart
 * Y-axis labels where the data has been pre-normalized on the backend (e.g.
 * `/company-pe-ratio-history`, `/company-price-to-fcf-history`).
 *
 * Same K/M/B + smart-decimals behaviour as `formatStockPriceCompact`, but
 * skips the pence→pound divide and always uses the display symbol — so a
 * GBX-flagged 1.66 renders as "£1.66" rather than "2p".
 */
export const formatDisplayCurrencyValueCompact = (
  price: number | null | undefined,
  currency?: string
): string => {
  if (price == null || isNaN(price)) return '0';

  const symbol = getDisplayCurrencySymbol(currency);

  if (Math.abs(price) >= 1_000_000_000) {
    return `${symbol}${(price / 1_000_000_000).toFixed(1)}B`;
  }
  if (Math.abs(price) >= 1_000_000) {
    return `${symbol}${(price / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(price) >= 1_000) {
    return `${symbol}${(price / 1_000).toFixed(1)}K`;
  }

  // < 10: 2 decimals (e.g. £1.66); < 100: 1 decimal; >= 100: 0 decimals.
  const decimals = Math.abs(price) < 10 ? 2 : Math.abs(price) < 100 ? 1 : 0;
  return `${symbol}${price.toFixed(decimals)}`;
};

/**
 * Format a stock price for display in compact notation (used in charts, etc.)
 * This does NOT convert pence to pounds - use for Y-axis labels where raw values are shown
 */
export const formatStockPriceCompact = (
  price: number | null | undefined,
  currency?: string
): string => {
  if (price == null || isNaN(price)) return '0';

  const symbol = isPenceCurrency(currency)
    ? price >= 100 ? '£' : ''
    : getCurrencySymbol(currency);

  // For GBX, convert to pounds for compact display
  const displayValue = isPenceCurrency(currency) && price >= 100
    ? price / 100
    : price;

  if (Math.abs(displayValue) >= 1_000_000_000) {
    return `${symbol}${(displayValue / 1_000_000_000).toFixed(1)}B`;
  }
  if (Math.abs(displayValue) >= 1_000_000) {
    return `${symbol}${(displayValue / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(displayValue) >= 1_000) {
    return `${symbol}${(displayValue / 1_000).toFixed(1)}K`;
  }

  // Add 'p' suffix for small pence values
  if (isPenceCurrency(currency) && price < 100) {
    return `${displayValue.toFixed(0)}p`;
  }

  // Use smart decimals for small values to avoid large rounding errors
  // < 10: show 2 decimals (e.g., £1.50)
  // < 100: show 1 decimal (e.g., £15.5)
  // >= 100: show 0 decimals (e.g., £150)
  const decimals = Math.abs(displayValue) < 10 ? 2 : Math.abs(displayValue) < 100 ? 1 : 0;
  return `${symbol}${displayValue.toFixed(decimals)}`;
};
